import React, { useState } from "react";
import { Phone, Menu, X, Droplets, Calendar, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { Home, Hammer, Box, Activity, Layers } from "lucide-react";

interface HeaderProps {
  setIsConsultOpen: (open: boolean) => void;
}

const Header: React.FC<HeaderProps> = ({ setIsConsultOpen }) => {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  const baseURL = "https://www.luxuriyahomes.com";

  const menuItems = [
    { label: "ABOUT US", external: true, path: `${baseURL}/about` },
    { 
      label: "SERVICES", 
      dropdown: [
        { name: "Terrace Waterproofing", icon: <Home size={14} /> },
        { name: "Bathroom Waterproofing", icon: <Hammer size={14} /> },
        { name: "Basement Waterproofing", icon: <Box size={14} /> },
        { name: "Crack Repair", icon: <Activity size={14} /> },
        { name: "Roof Coating", icon: <Layers size={14} /> },
        { name: "Leak Detection & Consultation", icon: <Activity size={14} /> },
      ]
    },
    { label: "PROJECTS", external: true, path: `${baseURL}/projects` },
    { label: "CONTACT", external: true, path: `${baseURL}/contact` },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm shadow">
      <div className="flex items-center justify-between px-4 md:px-8 py-4">
        
        {/* LOGO */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer group">
          <div className="relative flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-blue-900 to-blue-600 shadow-lg ring-2 ring-amber-500/70 group-hover:ring-amber-600 transition-all duration-300">
            <Droplets size={22} className="text-white" />
          </div>
          <div className="leading-tight">
            <h1 className="text-xl font-extrabold tracking-wide bg-gradient-to-r from-amber-500 via-amber-600 to-amber-700 bg-clip-text text-transparent">
              Luxuriya
            </h1>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-600 font-semibold">
              Waterproofing
            </p>
          </div>
        </Link>

        {/* DESKTOP MENU */}
        <nav className="hidden md:flex items-center gap-8">
          {menuItems.map((item) => (
            <div key={item.label} className="relative group">
              
              {item.dropdown ? (
                <button
                  type="button"
                  className="relative text-[16px] font-semibold text-gray-700 transition-all duration-300 hover:text-amber-600 flex items-center gap-1"
                >
                  {item.label}
                  <ChevronDown size={14} />
                </button>
              ) : item.external ? (
                <a
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative text-[16px] font-semibold text-gray-700 transition-all duration-300 hover:text-amber-600"
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  to={item.path}
                  className="relative text-[16px] font-semibold text-gray-700 transition-all duration-300 hover:text-amber-600"
                >
                  {item.label}
                </Link>
              )}

              {/* Dropdown */}
              {item.dropdown && (
                <ul className="absolute top-full left-0 mt-2 w-64 bg-white shadow-xl rounded-lg opacity-0 group-hover:opacity-100 invisible group-hover:visible transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 z-50">
                  {item.dropdown.map((subItem, idx) => (
                    <li key={idx}>
                      <Link
                        to={`/${subItem.name.toLowerCase().replace(/ /g, "-")}`}
                        className="flex items-center gap-2 px-4 py-3 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors border-b last:border-b-0"
                      >
                        <span className="text-amber-500">{subItem.icon}</span>
                        {subItem.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href="tel:+917291832000"
            className="flex items-center gap-2 bg-gradient-to-r from-amber-500 to-amber-700 text-white px-4 py-2 rounded-lg font-semibold shadow-md hover:shadow-xl hover:scale-105 transition-all"
          >
            <Phone size={18} /> Call Now
          </a>

          <a
            href="https://wa.me/917291832000"
            target="_blank"
            className="bg-green-500 text-white px-4 py-2 rounded-lg hover:scale-110 transition-transform"
          >
            WhatsApp
          </a>

          <button
            onClick={() => setIsConsultOpen(true)}
            className="flex items-center gap-2 border-2 border-amber-600 text-amber-700 px-4 py-2 rounded-lg font-semibold hover:bg-amber-600 hover:text-white transition-all"
          >
            <Calendar size={18} /> Book Consult
          </button>
        </div>

        {/* MOBILE TOGGLE */}
        <button className="md:hidden" onClick={() => setOpen(!open)}>
          {open ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* MOBILE MENU */}
      {open && (
        <div className="md:hidden bg-white shadow-lg px-4 py-4 space-y-4">
          {menuItems.map((item) => (
            <div key={item.label}>
              <div 
                className="flex justify-between items-center py-2 text-gray-700 font-semibold cursor-pointer"
                onClick={() => item.dropdown ? setServicesOpen(!servicesOpen) : setOpen(false)}
              >
                {item.dropdown ? (
                  <span>{item.label}</span>
                ) : item.external ? (
                  <a href={item.path} target="_blank">
                    {item.label}
                  </a>
                ) : (
                  <Link to={item.path} onClick={() => setOpen(false)}>
                    {item.label}
                  </Link>
                )}
                {item.dropdown && <ChevronDown size={18} />}
              </div>

              {item.dropdown && servicesOpen && (
                <ul className="pl-4">
                  {item.dropdown.map((subItem) => (
                    <li key={subItem.name}>
                      <Link
                        to={`/${subItem.name.toLowerCase().replace(/ /g, "-")}`}
                        onClick={() => setOpen(false)}
                        className="block py-2 text-gray-700 hover:text-amber-600"
                      >
                        {subItem.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </header>
  );
};

export default Header;

